import { useCallback, useEffect, useState } from "react";
import { fetchMyTrainingHistory } from "../api/fetchMyTrainingHistory/fetchMyTrainingHistoryApi";
import {
  FetchMyTrainingHistoryParams,
  MyTrainingHistoryItem,
} from "../api/fetchMyTrainingHistory/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

interface Pagination {
  pageIndex: number;
  pageSize: number;
}

function resolveSortBy(
  sorting: SortingState,
): FetchMyTrainingHistoryParams["sortBy"] | undefined {
  if (sorting.length === 0) return undefined;
  const sortId = sorting[0].id;
  if (
    sortId === "sessionStartDate" ||
    sortId === "eventName" ||
    sortId === "locationName"
  ) {
    return sortId;
  }
  return undefined;
}

export function useMyTrainingHistory() {
  const [items, setItems] = useState<MyTrainingHistoryItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params: FetchMyTrainingHistoryParams = {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    };

    const sortBy = resolveSortBy(sorting);
    if (sortBy) {
      params.sortBy = sortBy;
      params.sortOrder = sorting[0].desc ? "DESC" : "ASC";
    }

    fetchMyTrainingHistory(params)
      .then((response) => {
        if (cancelled) return;
        setItems(response.data);
        setTotalCount(response.meta?.totalCount ?? 0);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pagination, sorting]);

  const handlePaginationChange = useCallback((newPagination: Pagination) => {
    setLoading(true);
    setPagination(newPagination);
  }, []);

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setLoading(true);
      setSorting((prev) =>
        typeof updater === "function" ? updater(prev) : updater,
      );
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [],
  );

  return {
    items,
    totalCount,
    loading,
    pagination,
    sorting,
    handlePaginationChange,
    handleSortingChange,
  };
}
